import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import MonitorProduct from './Components/MonitorProducts';
import CategorySlider from './Components/CategorySlider';
import SiteSettings from './site-settings';
import LoadMoreButton from './Components/LoadMoreButton';
import Test from './Components/Test';
import './JsScripts/Application';

SiteSettings.orderHistory.showOnlyMyOrders = false;
SiteSettings.orderHistory.enableReorder = false;

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    // if (document.getElementById('showVat')) {
    //     renderReact(
    //         <Provider store={store}>
    //             <ShowVat />
    //         </Provider>,
    //         document.getElementById('showVat')
    //     );
    // }

    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('load-more-button')) {
        const loadMoreButton = document.getElementById('load-more-button');
        const { pageCount } = loadMoreButton.dataset;

        renderReact(
            <Provider store={store}>
                <LoadMoreButton {...{ pageCount }} />
            </Provider>,
            loadMoreButton
        );
    }
    if (document.getElementById('test')) {
        const test = document.getElementById('test');
       

        renderReact(
            <Provider store={store}>
                <Test />
            </Provider>,
            test
        );
    }

    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(
            ({ lightboxImages }) => {
                store.injectReducer('lightboxImages', lightboxImages);
                const rootElement = document.getElementById('lightBoxImages');
                const images = Array.from(
                    rootElement.querySelectorAll('a')
                ).map((img) => ({ src: img.dataset.src }));
                const thumbnails = Array.from(
                    rootElement.querySelectorAll('a img')
                ).map((img) => ({ src: img.src }));
                renderReact(
                    <Provider store={store}>
                        <LightboxImages
                            images={images}
                            thumbnails={thumbnails}
                        />
                    </Provider>,
                    document.getElementById('lightBoxImages')
                );
            }
        );
    }
    if (document.querySelectorAll('.monitorProduct').length > 0) {
        const monitorProducts = DynamicComponent({
            loader: () => import('./Components/MonitorProducts'),
        });
        Array.from(document.querySelectorAll('.monitorProduct')).forEach(
            (product) => {
                const { text, instock, variantid, id } = product.dataset;

                renderReact(
                    <Provider store={store}>
                        <MonitorProduct
                            {...{
                                text,
                                instock,
                                variantid,
                                id,
                            }}
                        />
                    </Provider>,
                    product
                );
            }
        );
    }
    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [
                    ...slider.querySelectorAll('.slider__block'),
                ].map((block) => {
                    return {
                        image: block.dataset.image,
                        url: block.dataset.url,
                        text: block.dataset.text,
                        actionText: block.dataset.actionText,
                    };
                });
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            }
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });

        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    articleName,
                    brand,
                    articleImageUrl,
                    minimumquantity,
                    isminimumquantity,
                    maximumquantity,
                    ismaximumquantity,
                    quantitystepping,
                    isquantitystepping,
                    articlestatus,
                    ispreorder,
                    href,
                    cssClass,
                    label,
                } = button.dataset;

                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                articleName,
                                brand,
                                articleImageUrl,
                                minimumquantity,
                                isminimumquantity,
                                maximumquantity,
                                ismaximumquantity,
                                quantitystepping,
                                isquantitystepping,
                                articlestatus,
                                ispreorder,
                                href,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }
    if (document.querySelectorAll('.imageslider').length > 0) {
        const ImageSlider = DynamicComponent({
            loader: () => import('./Components/ImageSlider'),
        });
        Array.from(document.querySelectorAll('.imageslider')).forEach(
            (slider, index) => {
                const values = [
                    ...slider.querySelectorAll('.slider__block'),
                ].map((block) => {
                    return {
                        image: block.innerHTML,
                        imageFull: block.dataset.imageFull,
                        video: block.dataset.video,
                    };
                });
                if (values.length > 0) {
                    renderReact(<ImageSlider values={values} />, slider);
                }
            }
        );
    }
    if (document.getElementById('magasinproductSpecification')) {
        const Specification = DynamicComponent({
            loader: () => import('./Components/Specification'),
        });
        const specificationElement = document.getElementById(
            'magasinproductSpecification'
        );

        const specifications = specificationElement.querySelector('div').dataset
            .specifications;
        const title = specificationElement.querySelector('div').dataset.title;
        
        renderReact(
            <Provider store={store}>
                <Specification specifications={specifications} title={title} />
            </Provider>,
            specificationElement
        );
    }

    if (document.getElementById('lipscoreReviews')) {
        const Lipscore = DynamicComponent({
            loader: () => import('./Components/Lipscore'),
        });
        const lipscoreTest = document.getElementById('lipscoreReviews');

        const productName = lipscoreTest.querySelector('div').dataset.productName;
        const productId = lipscoreTest.querySelector('div').dataset.productId;
        const productUrl = lipscoreTest.querySelector('div').dataset.productUrl;
        const variantId = lipscoreTest.querySelector('div').dataset.variantId;
        const variantName = lipscoreTest.querySelector('div').dataset.variantName;

        renderReact(
            <Provider store={store}>
                <Lipscore productName={productName} productId={productId} productUrl={productUrl} variantId={variantId} variantName={variantName} />
            </Provider>,
            lipscoreTest
        );
    }

    if (document.getElementById('upsellproducts')) {
        const UpsellProducts = DynamicComponent({
            loader: () => import('./Components/UpsellProducts'),
        });
        const sourceElement = document.getElementById('upsellproducts');
        const lightSources = JSON.parse(sourceElement.dataset.lightSources);
        const lampPlugs = JSON.parse(sourceElement.dataset.lampPlugs);
        const ownerVariantId = sourceElement.dataset.ownerVariantId;
        const quantityFieldId = sourceElement.dataset.quantityfieldid;
        renderReact(
            <Provider store={store}>
                <UpsellProducts
                    ownerVariantId={ownerVariantId}
                    lightSources={lightSources}
                    lampPlugs={lampPlugs}
                    quantityFieldId={quantityFieldId}
                />
            </Provider>,
            sourceElement
        );
    }
    if (document.getElementById('siteModal')) {
        const SiteModal = DynamicComponent({
            loader: () => import('./Components/SiteModal'),
        });
        const docElement = document.getElementById('siteModal');
        const {content, contentType }= docElement.querySelector('div').dataset;
      
        renderReact(
            <Provider store={store}>
                <SiteModal {...{ content, contentType }} />
            </Provider>,
            docElement
        );
    }
    if (document.getElementById('productDocuments')) {
        const ProductSheetDocuments = DynamicComponent({
            loader: () => import('./Components/ProductSheetDocuments'),
        });
        const docElement = document.getElementById('productDocuments');
        const documents = docElement.querySelector('div').dataset.documents;
        const title = docElement.querySelector('div').dataset.title;
        renderReact(
            <Provider store={store}>
                <ProductSheetDocuments documents={documents} title={title} />
            </Provider>,
            docElement
        );
    }
    if (document.getElementById('productVideos')) {
        const ProductVideos = DynamicComponent({
            loader: () => import('./Components/ProductVideos'),
        });
        const videoElement = document.getElementById('productVideos');
        const internal = videoElement.querySelector('div').dataset.internal;
        const title = videoElement.querySelector('div').dataset.title;
        const vimeo = videoElement.querySelector('div').dataset.vimeo;
        const youtube = videoElement.querySelector('div').dataset.youtube;
        renderReact(
            <Provider store={store}>
                <ProductVideos
                    title={title}
                    internalVideo={internal}
                    vimeo={vimeo}
                    youtube={youtube}
                />
            </Provider>,
            videoElement
        );
    }
    if (document.querySelectorAll('.category-slider').length > 0) {
        const ImageSlider = DynamicComponent({
            loader: () => import('./Components/CategorySlider'),
        });
        Array.from(document.querySelectorAll('.category-slider')).forEach(
            (slider, index) => {
                const values = [
                    ...slider.querySelectorAll('.slider__block'),
                ].map((block) => {
                    return {
                        image: block.dataset.image,
                        name: block.dataset.name,
                        url: block.dataset.url,
                    };
                });
                if (values.length > 0) {
                    renderReact(<CategorySlider values={values} />, slider);
                }
            }
        );
    }
    if (document.querySelector('#fb-conversion')) {
        const Conversion = DynamicComponent({
            loader: () => import('./Components/Conversion'),
        });
        const conversion = document.querySelector('#fb-conversion');
        const { event, eventName, customer } = conversion.dataset;
        renderReact(
            <Provider store={store}>
                <Conversion {...{ event, eventName, customer }} />
            </Provider>,
            conversion
        );
        conversion.dataset.event = '';
        conversion.dataset.eventName = '';
        conversion.dataset.customer = '';
    }
};

bootstrapComponents();
